/**
 * Plausible Analytics helper.
 *
 * Plausible is a privacy-first analytics service:
 *   - No cookies, no persistent identifiers, no cross-site tracking
 *   - GDPR/CCPA compliant by design — no consent banner required
 *   - Script is 1 KB, loads async, never blocks the page
 *
 * The script tag itself is added in layout.tsx. This module just provides
 * a typed `track(event, props)` helper for firing custom events.
 *
 * Custom events we track:
 *   - `Calculated` — when a Calculate completes successfully.
 *     Props: origin_state, dest_state, vehicle, overrode_mpg (boolean),
 *            waypoint_count, fuel_type, source (cached | fresh)
 *   - `Shared` — when the share link is copied.
 *   - `ExportedPDF` — when a PDF is downloaded.
 *   - `OverrodeMPG` — when a user enters a custom MPG value.
 *   - `OverrodeTankCapacity` — when a user enters a custom tank capacity for the fuel-stop optimizer.
 *   - `OverrodeHeight` — when a user enters their own vehicle height for bridge-aware routing.
 *   - `EnabledAuxPower` — when a user turns on generator/reefer fuel.
 *   - `OverrodeGph` — when a user enters their own generator/reefer burn rate.
 *   - `DonateClicked` — when the header donate button opens the Stripe page.
 *
 * Why this pattern: Plausible exposes `window.plausible()` globally, but
 * referencing `window` directly is fragile (SSR, missing script, type
 * narrowing). This helper gives us a single safe entry point with types.
 */

/** Allowed custom event names — keeping them centralized helps the Plausible dashboard stay clean */
export type AnalyticsEvent =
	| 'Calculated'
	| 'Shared'
	| 'ExportedPDF'
	| 'OverrodeMPG'
	| 'OverrodeTankCapacity'
	| 'OverrodeHeight'
	| 'EnabledAuxPower'
	| 'OverrodeGph'
	| 'FeedbackOpened'
	| 'FeedbackSubmitted'
	| 'DonateClicked'

/** Shape of the global Plausible function that the script injects */
interface PlausibleFn {
	(event: string, options?: { props?: Record<string, string | number | boolean> }): void
	q?: unknown[]
}

declare global {
	interface Window {
		plausible?: PlausibleFn
	}
}

/**
 * Fire a custom analytics event. Safe to call from anywhere — does nothing
 * if the Plausible script hasn't loaded yet (e.g., ad blocker, SSR).
 */
export function track(
	event: AnalyticsEvent,
	props?: Record<string, string | number | boolean>,
): void {
	if (typeof window === 'undefined') return
	if (!window.plausible) return
	try {
		window.plausible(event, props ? { props } : undefined)
	} catch {
		// Analytics failures never impact the user — swallow silently
	}
}
